import React, { useState, useEffect } from 'react'
import { investorServices, investmentServices } from '../../services'
import { Role, SearchParams, InvestorPropsV1 } from '../../lib/types'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { showBanner } from '../../global-state/banner-slice'
import './investors-list.scss'
import { InvestorsListPresenter } from './investors-list-presenter'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'

const InvestorsListContainer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [investors, setInvestors] = useState<InvestorPropsV1[]>()
  const [searchParams, setSearchParams] = useState<SearchParams>({
    limit: '10',
    offset: '0',
  })
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    count: 5,
    offset: 0,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [investorData, setInvestorData] = useState<InvestorPropsV1>()

  useEffect(() => {
    fetchInvestorsList()
  }, [currentPage, searchParams])

  const fetchInvestorsList = async () => {
    const data: any = await investorServices.getInvestorsList(Role.investor, searchParams)
    if (data && data.data && data.status === 200) {
      setInvestors(data.data)
      setPagination(data.pagination)
    }
  }

  const setInvestmentLimit = async (investorId: number, investmentLimit: number) => {
    // converting dollar value in cents
    const investmentLimitInCents = investmentLimit * 100
    const data: any = (await investmentServices.setInvestmentLimit(
      investorId,
      investmentLimitInCents,
    )) as InvestorPropsV1
    if (data && data.status === 200) {
      dispatch(
        showBanner({
          text: 'Investment limit updated',
          variant: 'success-banner',
        }),
      )
      fetchInvestorsList()
    } else {
      dispatch(
        showBanner({
          text:
            (data.data && data.data.message) ||
            'Something went wrong while updating investment limit',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleSearchParams = async ({ name, value }: { name: string; value: string }) => {
    const { limit } = pagination
    setSearchParams({
      ...searchParams,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    })
    setCurrentPage(1)
    setPagination({ ...pagination, offset: 0 })
  }

  const resetSearchParams = () => {
    setSearchParams({
      limit: '10',
      offset: '0',
      filterBy: '',
      keyword: '',
      account_status: '',
      signupFrom: '',
      signupTo: '',
      sortBy: '',
    })
  }

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = pagination
    setCurrentPage(currentPage)
    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })
    setPagination({ ...pagination, offset: (currentPage - 1) * limit })
  }

  const handleInvestorProfileClick = (email: string, investorId: number) => {
    logCrmEvent(EngagementEvents.CLICKED_MANAGE_PROFILE, {
      investorEmail: email,
      investorId: investorId,
      page: 'investors-list',
    })

    navigate(`/client/${investorId}/profile`)
  }

  const handleInvestorInvestmentsClick = (email: string, investorId: number) => {
    logCrmEvent(EngagementEvents.CLICKED_INVESTOR_INVESTMENTS, {
      investorEmail: email,
      investorId: investorId,
      page: 'investors-list',
    })

    navigate(`/user/investments/${investorId}`)
  }

  const handleInvestorWithdrawalClick = (email: string, investorId: number) => {
    logCrmEvent(EngagementEvents.CLICKED_INVESTOR_WITHDRAWALS, {
      investorEmail: email,
      investorId: investorId,
      page: 'investors-list',
    })

    navigate(`/user/withdrawals/${investorId}`)
  }

  const handleInvestorDataChange = (data: InvestorPropsV1) => {
    setInvestorData(data)
  }

  const handleUpdateInvestmentLimit = (investorId: number, investmentLimit: number) => {
    setInvestmentLimit(investorId, investmentLimit)
  }

  return (
    <InvestorsListPresenter
      investors={investors!}
      investorData={investorData!}
      pagination={pagination}
      currentPage={currentPage}
      searchParams={searchParams}
      handleCurrentPage={handleCurrentPage}
      handleSearchParams={handleSearchParams}
      resetSearchParams={resetSearchParams}
      handleInvestorDataChange={handleInvestorDataChange}
      handleInvestorProfileClick={handleInvestorProfileClick}
      handleInvestorInvestmentsClick={handleInvestorInvestmentsClick}
      handleInvestorWithdrawalClick={handleInvestorWithdrawalClick}
      handleUpdateInvestmentLimit={handleUpdateInvestmentLimit}
    />
  )
}

export { InvestorsListContainer }
